"use client";

import { useEventConnect } from "@/store/eventConnect";
import { useUserInfo } from "@/store/user-info";
import { usdToCentAmount } from "@/utils/cent-money";
import { resolveUnitPriceNumber } from "@/utils/price";
import { BigMath, getGuessDisplayTitle } from "@yn/utils";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { createEmptyBookProfitStopLossDraft } from "../book-profit-stop-loss/logic";
import { BUY_MAX_SHARES, SELL_MAX_SHARES } from "../components/const";
import { getMarketSellMaxShares, getSellMaxShares } from "../sellQuantity";
import type {
    ExpirationType,
    OpenTradePanelParams,
    OrderBookData,
    OrderType,
    SubmitStatus,
    TradePanelStore,
    TradeType,
} from "../types";
import { normalizeLimitPrice } from "../utils/normalizeLimitPrice";
import { getOrderTypeMemory, setOrderTypeMemory } from "./use-order-type-memory";

const DEFAULT_LIMIT_SHARES = 5;
const DEFAULT_MAX_SHARES = 2000;
const DEFAULT_MARKET_AMOUNT_CENT = usdToCentAmount( 10 );
const NEW_USER_MARKET_ORDER_TYPE_START_TIME = 1779733800000;

// 计算限价买入时的最大 shares（考虑余额和价格上限）
function calculateMaxSharesForBuy( limitPrice: number ): number {
    if ( BigMath.lte( limitPrice, 0 ) ) return DEFAULT_MAX_SHARES;

    const userBalanceCents = Number( useUserInfo.getState().userBalance ) || 0;
    if ( BigMath.lte( userBalanceCents, 0 ) ) return DEFAULT_MAX_SHARES;

    return Math.min( Number( BigMath.div( userBalanceCents, limitPrice, 0 ) ), DEFAULT_MAX_SHARES );
}

// 计算应该设置的 shares 初始值（考虑上限）
function getInitialShares( orderType: OrderType, limitPrice: number, tradeType: TradeType ): number {
    if ( orderType !== "limit" ) return 0;
    if ( tradeType !== "buy" ) return DEFAULT_LIMIT_SHARES;

    const maxShares = calculateMaxSharesForBuy( limitPrice );
    return BigMath.lt( maxShares, DEFAULT_LIMIT_SHARES ) ? maxShares : DEFAULT_LIMIT_SHARES;
}

function normalizeCreateTime( createTime: number | string | null | undefined ): number | undefined {
    const time = Number( createTime );
    if ( Number.isFinite( time ) && BigMath.gt( time, 0 ) ) {
        return BigMath.lt( time, 100000000000 ) ? Number( BigMath.times( time, 1000 ) ) : time;
    }

    if ( typeof createTime === "string" ) {
        const parsedTime = Date.parse( createTime );
        return Number.isFinite( parsedTime ) ? parsedTime : undefined;
    }

    return undefined;
}

function getDefaultOrderTypeByCreateTime(): OrderType {
    const createTime = normalizeCreateTime( useUserInfo.getState().createTime );
    if ( createTime === undefined ) return "market";
    return BigMath.gte( createTime, NEW_USER_MARKET_ORDER_TYPE_START_TIME ) ? "market" : "limit";
}

function getInitialOrderType( params: OpenTradePanelParams, tradeType: TradeType ): OrderType {
    if ( tradeType === "split" || tradeType === "merge" ) return "market";
    if ( params.orderType ) return params.orderType;
    return getOrderTypeMemory() ?? getDefaultOrderTypeByCreateTime();
}

function getRealtimePrice( guessInfo: any, selectedOption: "yes" | "no" ) {
    return selectedOption === "yes" ? guessInfo?.yesRealtimePrice : guessInfo?.noRealtimePrice;
}

function getInitialLimitPrice( params: OpenTradePanelParams, selectedOption: "yes" | "no" ) {
    if ( params.limitPrice !== undefined && params.limitPrice !== null ) {
        return normalizeLimitPrice( params.limitPrice );
    }
    return normalizeLimitPrice( resolveUnitPriceNumber( getRealtimePrice( params.guessInfo, selectedOption ) ) );
}

function toFiniteNumber( value: unknown ) {
    const numericValue = Number( value ?? 0 );
    return Number.isFinite( numericValue ) ? numericValue : 0;
}

function getOptionQuantityFromCostSource( source: any, selectedOption: "yes" | "no" ) {
    if ( !source ) return 0;

    return selectedOption === "yes"
        ? toFiniteNumber( source.yesSharesAmount ?? source.yesQuantity )
        : toFiniteNumber( source.noSharesAmount ?? source.noQuantity );
}

function getOptionAverageCost( source: any, selectedOption: "yes" | "no" ) {
    if ( !source ) return 0;

    const sideAverageCost = selectedOption === "yes" ? source.yesAvgPrice : source.noAvgPrice;
    if ( sideAverageCost !== undefined ) {
        const sideAverageCostNumber = toFiniteNumber( sideAverageCost );
        if ( BigMath.gt( sideAverageCostNumber, 0 ) ) return sideAverageCostNumber;
    }

    const sideQuantity = getOptionQuantityFromCostSource( source, selectedOption );
    if ( BigMath.lte( sideQuantity, 0 ) ) return 0;

    return toFiniteNumber( source.averagePrice ?? source.avgTradePrice ?? source.avgPrice ?? source.price );
}

function getSellOptionCost( userContractsInfo: any, positionInfo: any, selectedOption: "yes" | "no" ) {
    const userContractsCost = getOptionAverageCost( userContractsInfo, selectedOption );
    if ( BigMath.gt( userContractsCost, 0 ) ) return userContractsCost;

    return getOptionAverageCost( positionInfo, selectedOption );
}

function getFirstNonEmptyText( ...values: Array<string | number | null | undefined> ): string {
    for ( const value of values ) {
        const text = String( value ?? "" ).trim();
        if ( text ) return text;
    }
    return "";
}

function normalizeOpenTradePanelParams( params: OpenTradePanelParams ): OpenTradePanelParams {
    const eventInfo = params.eventInfo ?? {};
    const guessInfo = params.guessInfo ?? {};
    const detailInfo = params.detailInfo as any;
    const detailEvent = Array.isArray( detailInfo ) ? null : detailInfo?.event;
    const positionInfo = params.positionInfo as any;
    const eventCategory = eventInfo.eventCategory
        ?? detailInfo?.eventCategory
        ?? detailEvent?.eventCategory
        ?? positionInfo?.eventCategory;
    const eventImageUrl = getFirstNonEmptyText(
        eventInfo.imageUrl,
        eventInfo.eventImageUrl,
        detailInfo?.imageUrl,
        detailInfo?.eventImageUrl,
        detailEvent?.imageUrl,
        detailEvent?.eventImageUrl,
        positionInfo?.eventImageUrl,
        positionInfo?.imageUrl,
    );
    const groupTitle = getFirstNonEmptyText(
        guessInfo.groupTitle,
        positionInfo?.groupTitle,
        detailInfo?.groupTitle,
        detailEvent?.groupTitle,
    );
    const guessTitle = getFirstNonEmptyText(
        guessInfo.guessTitle,
        positionInfo?.guessTitle,
        guessInfo.title,
        detailInfo?.guessTitle,
        detailInfo?.title,
    );
    const displayTitle = getGuessDisplayTitle( { groupTitle, guessTitle } );

    return {
        ...params,
        eventInfo: {
            ...eventInfo,
            ...( eventCategory !== undefined ? { eventCategory } : {} ),
            ...( eventImageUrl ? { imageUrl: eventImageUrl } : {} ),
        },
        guessInfo: {
            ...guessInfo,
            ...( groupTitle ? { groupTitle } : {} ),
            ...( guessTitle ? { guessTitle } : {} ),
            ...( displayTitle ? { displayTitle } : {} ),
        },
    };
}

const initialState = {
    isOpen: false,
    tradeType: "buy" as TradeType,
    initialTradeType: null as TradeType | null,
    orderType: "market" as OrderType,
    selectedOption: "yes" as "yes" | "no",
    hasExplicitSelectedOption: false,
    amount: 0,
    limitPrice: 0,
    shares: 0,
    prevShares: 0,
    expirationEnabled: false,
    expirationType: "until-cancelled" as ExpirationType,
    customExpiration: null as number | null,
    eventInfo: null,
    guessInfo: null,
    detailInfo: null,
    positionInfo: null,
    userContractsInfo: null,
    orderbookData: null as OrderBookData | null,
    submitStatus: "idle" as SubmitStatus,
    orderNo: null as string | null,
    orderData: null,
    onSuccessCallback: null as (() => void) | null,
    pathname: null as string | null,
    quickToQuantityValue: null,
    cost: 0,
    recordNo: null as string | null,
    sellAll: false,
    readonlySellAll: false,
    maxSellQty: null as number | null,
    _ownsEventSubscription: false,
    bpSlDraft: createEmptyBookProfitStopLossDraft(),
};

export const useTradePanel = create<TradePanelStore>()( devtools( ( set, get ) => ( {
    ...initialState,

    /**
     * 打开交易弹窗并根据当前事件、订单簿和交易方向初始化表单状态。
     * 这里会同步计算默认下单类型、价格、数量，以及本次会话专属的 BP/SL 草稿。
     *
     * @param params 打开交易弹窗所需的上下文参数，包含事件信息、竞猜信息、默认交易方向和外部回调。
     * @returns 无返回值。
     */
    open: ( rawParams: OpenTradePanelParams ) => {
        const params = normalizeOpenTradePanelParams( rawParams );
        const ownsEventSubscription = !!params.eventInfo?.eventNo;
        const finalTradeType = params.tradeType ?? "buy";
        const initialTradeType = finalTradeType === "sell" ? "sell" : "buy";
        const finalOrderType = getInitialOrderType( params, finalTradeType );
        const selectedOption = params.selectedOption ?? "yes";
        const finalLimitPrice = getInitialLimitPrice( params, selectedOption );

        // 计算 shares：sellAll 时由 effect 根据 userContractsInfo 设置，此处置 0；外部传入则用；否则限价单按上限
        let finalShares: number;
        if ( params.sellAll ) {
            finalShares = 0;
        } else if ( params.shares !== undefined && params.shares !== null ) {
            finalShares = params.shares;
        } else if ( finalOrderType === "limit" ) {
            finalShares = getInitialShares( finalOrderType, finalLimitPrice, finalTradeType );
        } else if ( finalTradeType === "sell" ) {
            finalShares = getMarketSellMaxShares( {
                recordNo: params.recordNo,
                maxSellQty: params.maxSellQty,
                selectedOption,
                userContractsInfo: params.positionInfo,
            } );
        } else {
            finalShares = 0;
        }

        // 如果是卖出，需要同时考虑2000限制和持仓限制，取两者的最小值
        if ( finalTradeType === "sell" && selectedOption ) {
            const maxSharesForSell = finalOrderType === "market"
                ? getMarketSellMaxShares( {
                    recordNo: params.recordNo,
                    maxSellQty: params.maxSellQty,
                    selectedOption,
                    userContractsInfo: params.positionInfo,
                } )
                : getSellMaxShares( {
                    recordNo: params.recordNo,
                    maxSellQty: params.maxSellQty,
                    selectedOption,
                    userContractsInfo: params.positionInfo,
                } );

            if ( BigMath.gt( maxSharesForSell, 0 ) && BigMath.gt( finalShares, maxSharesForSell ) ) {
                finalShares = maxSharesForSell;
            } else if ( BigMath.gt( finalShares, SELL_MAX_SHARES ) ) {
                finalShares = SELL_MAX_SHARES;
            }
        } else {
            // 买入情况：检查是否超过上限（考虑余额和价格）
            if ( finalOrderType === "limit" && finalShares > 0 ) {
                const maxShares = calculateMaxSharesForBuy( finalLimitPrice );
                if ( finalShares > maxShares ) {
                    finalShares = maxShares;
                }
            } else if ( finalShares > BUY_MAX_SHARES ) {
                // 非限价单或没有价格时，只检查 2000 上限
                finalShares = BUY_MAX_SHARES;
            }
        }

        // 判断是否明确传入了 selectedOption
        const hasExplicitSelectedOption = params.selectedOption !== undefined;

        set( {
            isOpen: true,
            eventInfo: params.eventInfo,
            guessInfo: params.guessInfo,
            detailInfo: params.detailInfo ?? null,
            positionInfo: params.positionInfo ?? null,
            tradeType: finalTradeType,
            initialTradeType,
            orderType: finalOrderType,
            selectedOption,
            hasExplicitSelectedOption,
            limitPrice: finalOrderType === "limit" ? finalLimitPrice : 0,
            shares: finalShares,
            amount: finalTradeType === "buy" && finalOrderType === "market" ? DEFAULT_MARKET_AMOUNT_CENT : 0,
            submitStatus: "idle",
            orderNo: null,
            orderData: null,
            onSuccessCallback: params.onSuccess ?? null,
            pathname: params.pathname ?? null,
            orderbookData: params.orderbookData ?? null,
            quickToQuantityValue: null,
            cost: params.cost ?? 0,
            costShares: params.costShares ?? 0,
            recordNo: params.recordNo ?? null,
            sellAll: params.sellAll ?? false,
            readonlySellAll: params.readonlySellAll ?? false,
            maxSellQty: params.maxSellQty ?? null,
            _ownsEventSubscription: ownsEventSubscription,
            // 交易弹窗里的 BP/SL 只属于当前这次会话。
            // 每次 open 都重置，避免切换竞猜或重新打开时带上旧草稿。
            bpSlDraft: createEmptyBookProfitStopLossDraft(),
        } );
        useEventConnect.getState().subscribeEvent( params.eventInfo.eventNo );

        // 异步加载 orderbook；市价预估仅用于前端展示，真实撮合以后端结果为准。
        const checkOrderbook = async () => {
            const guessNo = params.guessInfo?.guessNo || "";

            // 如果已经传入了 orderbookData，不需要再次获取
            if ( params.orderbookData ) {
                return;
            }

            // 如果没有传入 orderbookData，则从 store 获取
            if ( guessNo ) {
                const eventConnect = useEventConnect.getState();
                await eventConnect.loadSnapshot( guessNo );
                const orderbookData = eventConnect.orderbookData[guessNo];
                const betType = selectedOption === "yes" ? 1 : 2;
                const mergedData = orderbookData?.[betType];
                if ( mergedData ) {
                    const currentState = get();

                    // 设置 orderbook 数据
                    if ( currentState.isOpen ) {
                        set( { orderbookData: mergedData } );
                    }
                }
                if ( finalTradeType === "buy" && finalOrderType === "limit" ) {
                    const betType = selectedOption === "yes" ? 1 : 2;
                    const orderbookData = eventConnect.orderbookData?.[guessNo]?.[betType];
                    const prices = orderbookData?.sellOrders?.map( item => item.price ) || [];
                    const minPrice = prices?.length > 0 ? Math.min( ...prices ) : 0;
                    if ( minPrice ) {
                        set( { limitPrice: normalizeLimitPrice( minPrice || params.limitPrice ) } );
                    }
                }
            }
        };

        // 执行异步检查
        checkOrderbook();
    },

    close: () => {
        const state = get();
        const eventNo = state.eventInfo?.eventNo ?? "";
        const eventConnect = useEventConnect.getState();

        if ( state._ownsEventSubscription && eventNo ) {
            eventConnect.unsubscribeEvent( eventNo );
        }

        set( { isOpen: false, orderbookData: null, _ownsEventSubscription: false } );
    },

    /**
     * 切换交易方向，并在需要时重新计算默认数量。
     * 当初始能力不允许卖出时，会直接拦截非法切换。
     *
     * @param type 目标交易方向，例如 buy、sell、split、merge。
     * @returns 无返回值。
     */
    setTradeType: ( type: TradeType ) => {
        // 切换交易类型时重置输入值
        const state = get();

        // 如果 initialTradeType !== "sell"，不允许切换到 "sell"
        if ( type === "sell" && state.initialTradeType !== "sell" ) {
            return;
        }

        if ( type === "split" || type === "merge" ) {
            set( { tradeType: type, orderType: "market", amount: 0, shares: 0 } );
        } else if ( state.orderType === "market" ) {
            const nextShares = type === "sell"
                ? getMarketSellMaxShares( {
                    recordNo: state.recordNo,
                    maxSellQty: state.maxSellQty,
                    selectedOption: state.selectedOption,
                    userContractsInfo: state.userContractsInfo || state.positionInfo,
                } )
                : 0;
            set( { tradeType: type, amount: type === "buy" ? DEFAULT_MARKET_AMOUNT_CENT : 0, shares: nextShares } );
        } else {
            // 在限价单模式下，切换 Buy/Sell 时，根据上限计算初始 shares
            const newShares = getInitialShares( state.orderType, state.limitPrice, type );
            set( { tradeType: type, amount: 0, shares: newShares } );
        }
    },

    /**
     * 切换订单类型，并根据当前价格与交易方向同步默认数量和记忆配置。
     * 从市价切到限价时，会尽量回填当前可用市价作为初始限价。
     *
     * @param type 目标订单类型，例如 market 或 limit。
     * @returns 无返回值。
     */
    setOrderType: ( type: OrderType ) => {
        const state = get();
        const updates: { orderType: OrderType; amount: number; limitPrice?: number; shares?: number } = {
            orderType: type,
            amount: type === "market" && state.tradeType === "buy" ? DEFAULT_MARKET_AMOUNT_CENT : 0,
        };

        if ( state.tradeType === "buy" || state.tradeType === "sell" ) {
            setOrderTypeMemory( type );
        }

        // 从市价单切换到限价单时，自动填充当前市价
        if ( type === "limit" && state.orderType === "market" && state.limitPrice === 0 ) {
            const { guessInfo, selectedOption } = state;
            if ( guessInfo ) {
                updates.limitPrice = normalizeLimitPrice(
                    resolveUnitPriceNumber( getRealtimePrice( guessInfo, selectedOption ) ),
                );
            }
        }

        if ( type === "limit" ) {
            if ( state.tradeType === "sell" ) {
                const holding = getSellMaxShares( {
                    recordNo: state.recordNo,
                    maxSellQty: state.maxSellQty,
                    selectedOption: state.selectedOption,
                    userContractsInfo: state.userContractsInfo || state.positionInfo,
                } );
                updates.shares = Math.min( holding, SELL_MAX_SHARES );
            } else if ( state.shares > 0 ) {
                // 如果已有 shares，检查是否超过上限
                const currentLimitPrice = updates.limitPrice ?? state.limitPrice;
                if ( state.tradeType === "buy" && currentLimitPrice > 0 ) {
                    const maxShares = calculateMaxSharesForBuy( currentLimitPrice );
                    updates.shares = Math.min( state.shares, maxShares );
                } else {
                    updates.shares = state.shares;
                }
            } else {
                // 如果 shares 为 0，根据上限计算初始值
                const currentLimitPrice = updates.limitPrice ?? state.limitPrice;
                updates.shares = getInitialShares( type, currentLimitPrice, state.tradeType );
            }
        } else if ( state.tradeType === "sell" ) {
            const holding = getMarketSellMaxShares( {
                recordNo: state.recordNo,
                maxSellQty: state.maxSellQty,
                selectedOption: state.selectedOption,
                userContractsInfo: state.userContractsInfo || state.positionInfo,
            } );
            updates.shares = Math.min( holding, SELL_MAX_SHARES );
        } else {
            updates.shares = 0;
        }

        set( updates );
    },

    /**
     * 切换当前下注选边，并根据卖出持仓、sellAll 状态或限价默认值同步数量。
     * 该方法会尽量保持用户已有输入，仅在选边变化导致约束变化时才修正数量。
     *
     * @param option 目标选边，只能是 yes 或 no。
     * @returns 无返回值。
     */
    setSelectedOption: ( option: "yes" | "no" ) => {
        const state = get();
        const userContractsInfo = state.userContractsInfo;
        const result: { selectedOption: "yes" | "no"; shares: number; cost?: number } = {
            selectedOption: option,
            shares: state.shares,
        };

        // sellAll 下切换选边时，shares 同步为新选边在 userContractsInfo 中的数量
        if ( state.tradeType === "sell" && state.sellAll && state.userContractsInfo ) {
            result.shares = getSellMaxShares( {
                recordNo: state.recordNo,
                maxSellQty: state.maxSellQty,
                selectedOption: option,
                userContractsInfo: state.userContractsInfo,
            } );
        } else if ( state.orderType === "limit" && state.shares === 0 ) {
            // 如果切换选项时，当前是限价单且 shares 为 0，则根据上限计算初始值
            result.shares = getInitialShares( state.orderType, state.limitPrice, state.tradeType );
        }
        if ( state.tradeType === "sell" ) {
            result.cost = getSellOptionCost( userContractsInfo, state.positionInfo, option );

            if ( !state.sellAll ) {
                const quantityGetter = state.orderType === "market" ? getMarketSellMaxShares : getSellMaxShares;
                const holding = quantityGetter( {
                    recordNo: state.recordNo,
                    maxSellQty: state.maxSellQty,
                    selectedOption: option,
                    userContractsInfo: userContractsInfo || state.positionInfo,
                } );
                if ( state.recordNo && state.maxSellQty !== null && state.maxSellQty !== undefined ) {
                    result.shares = Math.min( state.shares, holding, SELL_MAX_SHARES );
                } else {
                    result.shares = Math.min( holding, SELL_MAX_SHARES );
                }
            }
        }

        set( result );
    },

    /**
     * 更新市价单场景下的金额输入。
     *
     * @param value 用户输入的金额值。
     * @returns 无返回值。
     */
    setAmount: ( value: number ) => set( { amount: value } ),

    /**
     * 更新限价价格，并在写入前校验格式和数值有效性。
     * 一旦限价被手动修改，会清空 quick-to-quantity 快捷输入状态。
     *
     * @param value 用户输入的限价值。
     * @returns 无返回值。
     */
    setLimitPrice: ( value: number ) => {
        // 先用正则表达式验证字符串格式是否为有效的数字
        const isValidFormat = /^-?\d+(\.\d+)?$/.test( String( value ) );
        if ( !isValidFormat ) {
            return;
        }
        // 验证通过后，转换成数字
        const numValue = Number( value );
        // 再次检查转换结果是否有效
        if ( isNaN( numValue ) || !isFinite( numValue ) ) {
            return;
        }
        set( { limitPrice: normalizeLimitPrice( numValue ), quickToQuantityValue: null } );
    },

    /**
     * 更新当前下单数量，同时记录上一次有效数量，供快捷输入和联动逻辑复用。
     *
     * @param value 最新的下单数量。
     * @returns 无返回值。
     */
    setShares: ( value: number ) => {
        set( { shares: value, quickToQuantityValue: null, prevShares: value } );
    },

    /**
     * 切换是否启用过期时间设置。
     *
     * @param enabled 是否启用过期时间。
     * @returns 无返回值。
     */
    setExpirationEnabled: ( enabled: boolean ) => set( { expirationEnabled: enabled } ),

    /**
     * 更新过期时间类型。
     *
     * @param type 新的过期时间类型。
     * @returns 无返回值。
     */
    setExpirationType: ( type: ExpirationType ) => set( { expirationType: type } ),

    /**
     * 更新自定义过期时间时间戳。
     *
     * @param timestamp 自定义过期时间的毫秒时间戳；为空时表示清空。
     * @returns 无返回值。
     */
    setCustomExpiration: ( timestamp: number | null ) => set( { customExpiration: timestamp } ),

    /**
     * 写入当前用户持仓信息，供卖出和 sellAll 场景计算上限使用。
     *
     * @param info 最新的用户持仓信息。
     * @returns 无返回值。
     */
    setUserContractsInfo: ( info: any ) => set( { userContractsInfo: info } ),

    /**
     * 更新当前竞猜的订单簿快照数据。
     *
     * @param data 最新的订单簿数据；为空时表示清空。
     * @returns 无返回值。
     */
    setOrderbookData: ( data: OrderBookData | null ) => set( { orderbookData: data } ),

    /**
     * 更新提交态，并在有返回值时一并写入订单编号和订单详情。
     *
     * @param status 当前提交状态。
     * @param orderNo 可选的订单编号；未传时沿用现有值。
     * @param orderData 可选的订单详情；未传时沿用现有值。
     * @returns 无返回值。
     */
    setSubmitStatus: ( status: SubmitStatus, orderNo?: string, orderData?: any ) =>
        set( { submitStatus: status, orderNo: orderNo ?? get().orderNo, orderData: orderData ?? get().orderData } ),

    /**
     * 记录最近一次选择的买入/卖出订单类型，供下一次打开交易弹窗默认使用。
     *
     * @returns 无返回值。
     */
    recordCurrentOrderTypeMemory: ( orderType: OrderType ) => {
        setOrderTypeMemory( orderType );
    },

    /**
     * 更新交易弹窗内的 BP/SL 草稿。
     * 草稿被集中放在 panel store 中，方便 websocket 联动和提交流程直接消费。
     *
     * @param draft 最新的 BP/SL 草稿。
     * @returns 无返回值。
     */
    setBpSlDraft: ( draft ) => set( { bpSlDraft: draft } ),

    /**
     * 将交易弹窗 store 恢复到初始状态。
     * 该方法会同时清空 BP/SL 草稿、订单簿数据和当前表单输入。
     *
     * @returns 无返回值。
     */
    reset: () => set( initialState ),

    /**
     * 根据快捷金额反推数量，并记录当前快捷输入值。
     * 当快捷金额被清空时，会同步清掉 quickToQuantity 状态。
     *
     * @param value 快捷金额值。
     * @returns 无返回值。
     */
    setQuickToQuantityValue( value: number ) {
        if ( !value ) {
            set( { quickToQuantityValue: null } );
        } else {
            set( { quickToQuantityValue: value, shares: BigMath.div( value, get().limitPrice, 0 ) } );
        }
    },
} ), { name: "TradePanel", enabled: process.env.NODE_ENV === "development" } ) );
